import { emptyWatchTowerState } from './emptyWatchTowerState';
import { normalizeWatchAiSuggestion } from './lambdaSuggestion';

import type {
  IWatchIncidentAffected,
  IWatchIncidentDetail,
  IWatchIncidentListItem,
  IWatchIssueChatRecord,
} from 'store/slices/watchTower/interfaces/IWatchTower';
import type {
  WatchAiChat,
  WatchAiStatus,
  WatchAiSuggestion,
  WatchAssociation,
  WatchBuilding,
  WatchCompany,
  WatchCompanyStatus,
  WatchFinancial,
  WatchFinding,
  WatchFindingAffected,
  WatchIdentifier,
  WatchIssueChat,
  WatchOrgNode,
  WatchProduct,
  WatchSchedule,
  WatchScheduleTarget,
  WatchShareholding,
  WatchTargetKind,
  WatchTopic,
  WatchTopicMatchKind,
  WatchTopicMatchRules,
  WatchTopicStatus,
  WatchTowerMockState,
} from './types';

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asStringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function asNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return fallback;
}

function asNumberOrNull(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  const parsed = asNumber(value, Number.NaN);
  return Number.isFinite(parsed) ? parsed : null;
}

function asStringArray(value: unknown): string[] {
  return asArray(value).filter((item): item is string => typeof item === 'string');
}

function asIso(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  return '';
}

export function watchFindingIdFromPublicId(publicId: string): number {
  let hash = 0;
  for (let i = 0; i < publicId.length; i += 1) {
    hash = ((hash * 31) + publicId.charCodeAt(i)) % 2147483647;
  }
  return hash || 1;
}

function mapIdentifier(raw: unknown): WatchIdentifier | null {
  const row = asRecord(raw);
  if (!row) {
    return null;
  }
  const scheme = asString(row.scheme);
  const value = asString(row.value);
  if (!scheme || !value) {
    return null;
  }
  return { scheme, value };
}

function mapProduct(raw: unknown): WatchProduct | null {
  const row = asRecord(raw);
  if (!row) {
    return null;
  }
  return {
    id: asNumber(row.id),
    name: asString(row.name),
    code: asStringOrNull(row.code),
    uid: asString(row.uid),
    productCategory: asStringOrNull(row.productCategory),
    productType: asStringOrNull(row.productType),
    upstreamTier: asStringOrNull(row.upstreamTier),
    dateOpened: asStringOrNull(row.dateOpened),
    dateClosed: asStringOrNull(row.dateClosed),
    volumeQty: asNumberOrNull(row.volumeQty),
    volumeQtyUnit: asStringOrNull(row.volumeQtyUnit),
    relianceOnFreeNaturalResources: asStringArray(row.relianceOnFreeNaturalResources),
    relianceOnFreeSocialResources: asStringArray(row.relianceOnFreeSocialResources),
    criticalityRating: asStringOrNull(row.criticalityRating) ?? asNumberOrNull(row.criticalityRating),
    circularityFromDownstreamWaste: asNumberOrNull(row.circularityFromDownstreamWaste),
    hsCode: asStringOrNull(row.hsCode),
    description: asStringOrNull(row.description),
  };
}

function mapBuilding(raw: unknown): WatchBuilding | null {
  const row = asRecord(raw);
  if (!row) {
    return null;
  }
  return {
    id: asNumber(row.id),
    name: asString(row.name),
    uid: asString(row.uid),
    legalName: asStringOrNull(row.legalName),
    ownership: asStringOrNull(row.ownership),
    equityShareholding: asNumberOrNull(row.equityShareholding),
    financialControl: asNumberOrNull(row.financialControl),
    businessUnitType: asStringOrNull(row.businessUnitType),
    assetCategory: asStringArray(row.assetCategory),
    ownedLeased: asStringOrNull(row.ownedLeased),
    numberOfEmployees: asNumberOrNull(row.numberOfEmployees),
    dateOpened: asStringOrNull(row.dateOpened),
    dateClosed: asStringOrNull(row.dateClosed),
    startDate: asStringOrNull(row.startDate),
    endDate: asStringOrNull(row.endDate),
    naceSection: asStringOrNull(row.naceSection),
    naceCode: asStringOrNull(row.naceCode),
    euTaxonomyCode: asStringOrNull(row.euTaxonomyCode),
    netBookAmount: asNumberOrNull(row.netBookAmount),
    netBookCurrency: asStringOrNull(row.netBookCurrency),
    addressCountry: asStringOrNull(row.addressCountry),
    addressState: asStringOrNull(row.addressState),
    addressPostCode: asStringOrNull(row.addressPostCode),
    addressCity: asStringOrNull(row.addressCity),
    addressStreet: asStringOrNull(row.addressStreet),
    addressBuilding: asStringOrNull(row.addressBuilding),
    addressCoordinates: asStringOrNull(row.addressCoordinates),
  };
}

function mapOrgNode(raw: unknown): WatchOrgNode | null {
  const row = asRecord(raw);
  if (!row) {
    return null;
  }
  return {
    id: asNumber(row.id),
    name: asString(row.name),
    role: asString(row.role),
    parentId: asNumberOrNull(row.parentId),
    ownershipPct: asNumberOrNull(row.ownershipPct),
    relatedWatchCompanyId: asNumberOrNull(row.relatedWatchCompanyId),
    addressCountry: asStringOrNull(row.addressCountry),
  };
}

function mapAssociation(raw: unknown): WatchAssociation | null {
  const row = asRecord(raw);
  if (!row) {
    return null;
  }
  return {
    id: asNumber(row.id),
    eventKind: asString(row.eventKind),
    occurredOn: asString(row.occurredOn),
    effectiveFrom: asStringOrNull(row.effectiveFrom),
    effectiveTo: asStringOrNull(row.effectiveTo),
    relatedWatchCompanyId: asNumberOrNull(row.relatedWatchCompanyId),
    personUid: asStringOrNull(row.personUid),
    personFullName: asStringOrNull(row.personFullName),
    personNationality: asStringOrNull(row.personNationality),
    personDateOfBirth: asStringOrNull(row.personDateOfBirth),
    personRole: asStringOrNull(row.personRole),
    notes: asStringOrNull(row.notes),
    payloadJson: asRecord(row.payloadJson),
  };
}

function mapShareholding(raw: unknown): WatchShareholding | null {
  const row = asRecord(raw);
  if (!row) {
    return null;
  }
  return {
    holderName: asString(row.holderName),
    personUid: asStringOrNull(row.personUid),
    pct: asNumber(row.pct),
    kind: asString(row.kind),
  };
}

function mapFinancial(raw: unknown): WatchFinancial | null {
  const row = asRecord(raw);
  if (!row) {
    return null;
  }
  const shareholdingRaw = row.shareholdingJson;
  return {
    id: asNumber(row.id),
    periodStart: asString(row.periodStart),
    periodEnd: asString(row.periodEnd),
    reportKind: asString(row.reportKind),
    currency: asStringOrNull(row.currency),
    revenue: asNumberOrNull(row.revenue),
    ebitda: asNumberOrNull(row.ebitda),
    liquidity: asNumberOrNull(row.liquidity),
    marketValuation: asNumberOrNull(row.marketValuation),
    shareholdingJson: Array.isArray(shareholdingRaw)
      ? shareholdingRaw.map(mapShareholding).filter((item): item is WatchShareholding => Boolean(item))
      : null,
    sourceUrl: asStringOrNull(row.sourceUrl),
    notes: asStringOrNull(row.notes),
    payloadJson: asRecord(row.payloadJson),
  };
}

function mapAiSuggestion(raw: unknown, company: WatchCompany): WatchAiSuggestion | null {
  return normalizeWatchAiSuggestion(raw, company);
}

export function mapWatchCompany(raw: unknown): WatchCompany | null {
  const row = asRecord(raw);
  if (!row || !asNumber(row.id)) {
    return null;
  }
  const company: WatchCompany = {
    id: asNumber(row.id),
    name: asString(row.name),
    code: asStringOrNull(row.code),
    uid: asString(row.uid),
    ownership: asStringOrNull(row.ownership),
    dateOpened: asStringOrNull(row.dateOpened),
    dateClosed: asStringOrNull(row.dateClosed),
    naceSection: asStringOrNull(row.naceSection),
    naceCode: asStringOrNull(row.naceCode),
    euTaxonomyCode: asStringOrNull(row.euTaxonomyCode),
    addressCountry: asString(row.addressCountry),
    addressState: asStringOrNull(row.addressState),
    addressCity: asStringOrNull(row.addressCity),
    addressPostCode: asStringOrNull(row.addressPostCode),
    addressStreet: asStringOrNull(row.addressStreet),
    addressBuilding: asStringOrNull(row.addressBuilding),
    addressCoordinates: asStringOrNull(row.addressCoordinates),
    website: asStringOrNull(row.website),
    status: (asString(row.status, 'draft') as WatchCompanyStatus),
    notes: asStringOrNull(row.notes),
    identifiers: asArray(row.identifiers).map(mapIdentifier).filter((item): item is WatchIdentifier => Boolean(item)),
    aliases: asStringArray(row.aliases),
    products: asArray(row.products).map(mapProduct).filter((item): item is WatchProduct => Boolean(item)),
    buildings: asArray(row.buildings).map(mapBuilding).filter((item): item is WatchBuilding => Boolean(item)),
    orgNodes: asArray(row.orgNodes).map(mapOrgNode).filter((item): item is WatchOrgNode => Boolean(item)),
    associations: asArray(row.associations).map(mapAssociation)
      .filter((item): item is WatchAssociation => Boolean(item)),
    financials: asArray(row.financials).map(mapFinancial).filter((item): item is WatchFinancial => Boolean(item)),
    aiStatus: (asString(row.aiStatus, 'idle') as WatchAiStatus),
    aiErrorMessage: asStringOrNull(row.aiErrorMessage),
    aiSuggestion: null,
  };
  company.aiSuggestion = mapAiSuggestion(row.aiSuggestion, company)
    || mapAiSuggestion(row.aiSuggestionJson, company);
  return company;
}

export function mapWatchTopic(raw: unknown): WatchTopic | null {
  const row = asRecord(raw);
  if (!row || !asNumber(row.id)) {
    return null;
  }
  return {
    id: asNumber(row.id),
    uid: asString(row.uid),
    title: asString(row.title),
    briefMarkdown: asString(row.briefMarkdown),
    agentType: asString(row.agentType),
    subType: asString(row.subType),
    agentConfigId: asNumberOrNull(row.agentConfigId),
    templateName: asString(row.templateName),
    matchKinds: asArray(row.matchKinds).filter((item): item is WatchTopicMatchKind => typeof item === 'string'),
    matchRulesJson: (asRecord(row.matchRulesJson) || {}) as WatchTopicMatchRules,
    status: (asString(row.status, 'draft') as WatchTopicStatus),
    paused: Boolean(row.paused),
    createdAt: asIso(row.createdAt),
    lastRunAt: asStringOrNull(row.lastRunAt),
    briefChatId: asNumberOrNull(row.briefChatId),
    namedMisses: [],
  };
}

export function mapWatchSchedule(raw: unknown): WatchSchedule | null {
  const row = asRecord(raw);
  if (!row || !asNumber(row.id)) {
    return null;
  }
  const targets = asArray(row.watchTargets || row.targets).map((item) => {
    const target = asRecord(item);
    if (!target) {
      return null;
    }
    return {
      kind: asString(target.targetKind || target.kind, 'company'),
      id: asNumber(target.targetId || target.id),
      watchCompanyId: asNumber(target.watchCompanyId),
    };
  }).filter((item): item is { kind: string, id: number, watchCompanyId: number } => Boolean(item));
  const firstCompanyId = targets.find((item) => item.watchCompanyId)?.watchCompanyId
    || asNumber(row.companyId);
  return {
    id: asNumber(row.id),
    title: asString(row.title),
    watchCompanyId: firstCompanyId,
    watchTopicId: asNumberOrNull(row.watchTopicId),
    type: asString(row.type),
    subType: asString(row.subType),
    agentConfigId: asNumberOrNull(row.agentConfigId),
    templateName: asString(row.templateName),
    timezone: asString(row.timezone),
    hourOfDay: asNumber(row.hourOfDay),
    frequency: asString(row.frequency),
    paused: Boolean(row.paused),
    nextRunAt: asStringOrNull(row.nextRunAt),
    lastRunStatus: asStringOrNull(row.lastRunStatus),
    startDate: asStringOrNull(row.startDate),
    endDate: asStringOrNull(row.endDate),
    targetKind: targets[0]?.kind || 'company',
    targetIds: targets.map((item) => item.id),
    targets: targets.map((item): WatchScheduleTarget => ({
      kind: item.kind as WatchTargetKind,
      id: item.id,
    })),
  };
}

export function mapWatchIncident(item: IWatchIncidentListItem, detail?: IWatchIncidentDetail | null): WatchFinding {
  const { publicId } = item;
  const observedAt = asIso(item.createdAt);
  return {
    id: watchFindingIdFromPublicId(publicId),
    publicId,
    agentRunId: 0,
    watchCompanyId: item.watchCompanyId,
    sourceKind: (item.sourceKind === 'topic' ? 'topic' : 'company'),
    watchTopicId: item.watchTopicId,
    findingType: item.findingType,
    severity: item.severity,
    title: item.title,
    description: detail?.description ?? null,
    observedAt,
    confidenceScore: null,
    sourceUrl: null,
    payloadJson: detail?.payloadJson ?? item.payloadJson ?? null,
    elevated: item.elevated,
  };
}

export function mapWatchAffected(
  findingId: number,
  sourceWatchCompanyId: number | null,
  row: IWatchIncidentAffected,
): WatchFindingAffected {
  return {
    id: row.id,
    findingId,
    sourceWatchCompanyId: sourceWatchCompanyId || 0,
    affectedWatchCompanyId: row.affectedWatchCompanyId,
    relationKind: row.relationKind,
    watchProductId: null,
    flaggedBy: row.flaggedBy,
    status: row.status,
    owner: null,
    notifiedAt: row.notifiedAt,
    talkingPoint: null,
  };
}

export function mapWatchIssueChat(raw: IWatchIssueChatRecord): WatchIssueChat {
  return {
    id: raw.id,
    findingId: raw.findingId,
    title: raw.title,
    agentModelId: null,
    seedKind: raw.seedKind,
    seedAgentRunId: raw.seedAgentRunId,
    createdBy: String(raw.createdByUserId),
    aiChatId: raw.aiChatId,
  };
}

export function sessionCreateChat(
  bind: { chatId: number, aiChatId: number },
  title = 'New watch company',
): WatchAiChat {
  return {
    id: bind.chatId,
    title,
    taskKind: 'create_company',
    watchCompanyId: null,
    agentModelId: null,
    createdBy: 'live',
    committedAt: null,
    pendingSeeds: [],
    dossierSearchKind: null,
    aiChatId: bind.aiChatId,
  };
}

export function sessionDossierChat(
  bind: { chatId: number, aiChatId: number },
  company: { id: number, name: string },
): WatchAiChat {
  return {
    id: bind.chatId,
    title: `Talk — ${company.name}`,
    taskKind: 'company_dossier',
    watchCompanyId: company.id,
    agentModelId: null,
    createdBy: 'live',
    committedAt: null,
    pendingSeeds: [],
    dossierSearchKind: null,
    aiChatId: bind.aiChatId,
  };
}

export function sessionIssueChat(
  bind: { chatId: number, aiChatId: number },
  finding: WatchFinding,
): WatchIssueChat {
  return {
    id: bind.chatId,
    findingId: finding.id,
    title: finding.title,
    agentModelId: null,
    seedKind: 'manual',
    seedAgentRunId: null,
    createdBy: 'live',
    aiChatId: bind.aiChatId,
  };
}

export function mapWatchTowerLive(input: {
  companies?: unknown[] | null,
  companyDetail?: unknown,
  topics?: unknown[] | null,
  schedules?: unknown[] | null,
  incidents?: IWatchIncidentListItem[] | null,
  incidentDetail?: IWatchIncidentDetail | null,
  issueChats?: IWatchIssueChatRecord[] | null,
}): WatchTowerMockState {
  const companies = (input.companies || []).map(mapWatchCompany)
    .filter((item): item is WatchCompany => Boolean(item));
  const detail = mapWatchCompany(input.companyDetail);
  const watchCompanies = detail
    ? companies.map((item) => (item.id === detail.id ? { ...item, ...detail } : item))
      .concat(companies.some((item) => item.id === detail.id) ? [] : [detail])
    : companies;

  const findings = (input.incidents || []).map((item) => (
    input.incidentDetail && input.incidentDetail.publicId === item.publicId
      ? mapWatchIncident(item, input.incidentDetail)
      : mapWatchIncident(item)
  ));
  const extraDetail = input.incidentDetail
    && !findings.some((item) => item.publicId === input.incidentDetail?.publicId)
    ? [mapWatchIncident(input.incidentDetail, input.incidentDetail)]
    : [];
  const allFindings = [...findings, ...extraDetail];
  const detailFinding = allFindings.find((item) => item.publicId === input.incidentDetail?.publicId);
  const findingAffectedCompanies = (input.incidentDetail?.affectedCompanies || []).map((row) => (
    mapWatchAffected(detailFinding?.id || 0, input.incidentDetail?.watchCompanyId ?? null, row)
  ));

  return {
    ...emptyWatchTowerState(),
    watchCompanies,
    watchTopics: (input.topics || []).map(mapWatchTopic).filter((item): item is WatchTopic => Boolean(item)),
    schedules: (input.schedules || []).map(mapWatchSchedule)
      .filter((item): item is WatchSchedule => Boolean(item)),
    findings: allFindings,
    findingAffectedCompanies,
    issueChats: (input.issueChats || []).map(mapWatchIssueChat),
  };
}
